import {debounce} from "lodash";
import * as React from "react";
import {isIE, isIOS} from "react-device-detect";
import {connect} from "react-redux";
import * as Swipeable from "react-swipeable";
import Error from "src/components/Error";
import ScrollyComponent from "src/components/ScrollyComponent";
import {IContent, IScrollyContainer} from "src/ScrollyInterface";
import * as actions from "src/store/actions/index";

import {library} from "@fortawesome/fontawesome-svg-core";
import {fab} from "@fortawesome/free-brands-svg-icons";
import {
	faArrowAltCircleLeft,
	faBars,
	faTimes
} from "@fortawesome/free-solid-svg-icons";
import {Dispatch} from "redux";
import {
	IDisablePullToRefreshAction,
	IGetBounceAction,
	IGetContentAction,
	INavigationFinishedAction,
	IScrollFinishedAction,
	ISetMediaLoadedAction,
	ISetScrollDirectionAction,
	IStateApp,
	IToggleSideDrawerAction,
	IToggleSoundAction,
	IVideoIsLoadedAction
} from "src/store/StoreInterface";

library.add(fab, faArrowAltCircleLeft, faBars, faTimes);

const debounceTime = 500;

class ScrollyContainer extends React.Component<IScrollyContainer, {}> {
	public divRef: React.RefObject<HTMLDivElement>;
	private dispatchArrowUp: () => void;
	private dispatchArrowDown: () => void;
	private keyDownDebounced: (event: KeyboardEvent) => void;
	private wheelDebounced: (event: WheelEvent) => void;

	constructor(props: IScrollyContainer) {
		super(props);
		this.props.onGetContent(this.props.data.content[0]);
		this.divRef = React.createRef();
		this.keyDownDebounced = debounce(this.handleKeyDown, debounceTime, {
			leading: true,
			trailing: false
		});
		this.wheelDebounced = debounce(this.handleWheel, debounceTime, {
			leading: true,
			trailing: false
		});
	}

	public componentDidMount() {
		this.dispatchArrowUp = () => document.dispatchEvent(new Event("goUp"));
		this.dispatchArrowDown = () => document.dispatchEvent(new Event("goDown"));
		document.addEventListener("keydown", this.keyDownDebounced);
		document.addEventListener("goUp", this.keyDownDebounced);
		document.addEventListener("goDown", this.keyDownDebounced);
		if (this.divRef.current) {
			this.divRef.current.addEventListener("wheel", this.wheelDebounced, {
				passive: true
			});
		}
	}

	public componentWillUpdate(prevProps: IScrollyContainer) {
		if (prevProps.scrollClicked !== this.props.scrollClicked) {
			this.handleScrollClicked();
		}
	}

	public componentDidUpdate(prevProps: IScrollyContainer) {
		if (prevProps.mediaLoaded !== this.props.mediaLoaded) {
			this.props.onGetBounce(this.props.mediaLoaded);
		}
		if (prevProps.content.id !== this.props.content.id) {
			const muted = isIOS ? true : prevProps.muted;
			if (isIOS) {
				this.props.onUnmuted(muted);
			}
			this.props.onGetBounce(this.props.content.id === 0);
			if (isIE) {
				this.props.onVideoLoaded(false);
			}
		}
		if (
			this.props.navigationClicked &&
			prevProps.navigationClicked !== this.props.navigationClicked
		) {
			this.handleNavigation(this.props.id);
			this.props.onToggleSideDrawer(false);
		}
	}

	public componentWillUnmount() {
		if (this.divRef.current) {
			this.divRef.current.removeEventListener("wheel", this.wheelDebounced);
		}
		document.removeEventListener("keydown", this.keyDownDebounced);
		document.removeEventListener("goUp", this.keyDownDebounced);
		document.removeEventListener("goDown", this.keyDownDebounced);
	}

	public handleWheel = (event: WheelEvent) => {
		const calculateYIE = () => {
			let timeout: number | undefined;
			clearTimeout(timeout);
			timeout = window.setTimeout(() => {
				this.calculateY(event.deltaY);
			}, 150);
		};

		isIE ? calculateYIE() : this.calculateY(event.deltaY);
	};

	public handleNavigation = (id: number) => {
		const direction = this.props.content.id > this.props.id ? "up" : "down";
		this.props.onSetScrollDirection(direction);
		this.props.onGetContent(this.props.data.content[this.props.id]);
		this.props.onNavigationFinished();
	};

	public handleScrollClicked = () => {
		this.props.onSetScrollDirection("down");
		if (this.props.content.id < this.props.data.content.length) {
			this.props.onGetContent(
				this.props.data.content[this.props.content.id + 1]
			);
		}
		this.props.onScrollFinished();
	};

	public handleKeyDown = (event: KeyboardEvent) => {
		switch (event.type) {
			case "keydown":
				switch (event.key) {
					case "ArrowUp":
					case "Up":
						this.moveUp();
						break;
					case "ArrowDown":
					case "Down":
						this.moveDown();
						break;
					default:
						break;
				}
				break;
			case "goUp":
				this.moveUp();
				break;
			case "goDown":
				this.moveDown();
				break;
			default:
				break;
		}
	};

	public getImages = () => {
		return this.props.data.content.map((data: IContent) => {
			return data.thumbnail ? data.thumbnail : "";
		});
	};

	public calculateY = (deltaY: number) => {
		deltaY > 0 ? this.moveDown() : this.moveUp();
	};

	public moveDown = () => {
		if (this.props.content.id !== this.props.data.content.length - 1) {
			this.props.onSetScrollDirection("down");
			this.props.onGetContent(
				this.props.data.content[this.props.content.id + 1]
			);
		}
	};

	public moveUp = () => {
		if (this.props.content.id > 0) {
			this.props.onSetScrollDirection("up");
			this.props.onGetContent(
				this.props.data.content[this.props.content.id - 1]
			);
		}
	};

	public handleMediaLoaded = (mediaLoaded: boolean) => {
		this.props.onMediaLoaded(mediaLoaded);
	};

	public handleVideoLoaded = (videoLoaded: boolean) => {
		this.props.onVideoLoaded(videoLoaded);
	};

	public render() {
		const {
			bounce,
			content,
			data,
			error,
			id,
			muted,
			mediaLoaded,
			scrollDirection,
			showSideDrawer,
			videoLoaded
		} = this.props;

		const scrollyComponent = error ? (
			<Error error={error}/>
		) : (
			<ScrollyComponent
				bounce={bounce}
				content={content}
				elemNumber={data.content.length}
				footer={content.footer}
				header={content.header}
				id={id}
				links={data.links}
				navImages={this.getImages()}
				mediaLoaded={mediaLoaded}
				muted={muted}
				onMediaLoaded={this.handleMediaLoaded}
				onVideoLoaded={this.handleVideoLoaded}
				scrollDirection={scrollDirection}
				showSideDrawer={showSideDrawer}
				videoLoaded={videoLoaded}
			/>
		);

		return (
			<Swipeable
				onSwipedRight={this.dispatchArrowUp}
				onSwipedLeft={this.dispatchArrowDown}
				onSwipedDown={this.dispatchArrowUp}
				onSwipedUp={this.dispatchArrowDown}
				stopPropagation={true}
				preventDefaultTouchmoveEvent={true}
				disabled={this.props.showSideDrawer}
			>
				<div ref={this.divRef}>{scrollyComponent}</div>
			</Swipeable>
		);
	}
}

const mapStateToProps = (state: IStateApp) => {
	return {
		bounce: state.ui.bounce,
		breakpoint: state.ui.breakpoint,
		content: state.data.content,
		error: state.data.error,
		id: state.data.id,
		isFetching: state.data.isFetching,
		mediaLoaded: state.data.mediaLoaded,
		muted: state.ui.muted,
		navigationClicked: state.data.navigationClicked,
		pullToRefreshDisabled: state.ui.pullToRefreshDisabled,
		scrollClicked: state.data.scrollClicked,
		scrollDirection: state.data.scrollDirection,
		showSideDrawer: state.ui.showSideDrawer,
		videoLoaded: state.ui.videoLoaded
	};
};

const mapDispatchToProps = (
	dispatch: Dispatch<| IDisablePullToRefreshAction
		| IGetBounceAction
		| IGetContentAction
		| ISetMediaLoadedAction
		| INavigationFinishedAction
		| IScrollFinishedAction
		| ISetScrollDirectionAction
		| IToggleSideDrawerAction
		| IToggleSoundAction
		| IVideoIsLoadedAction>
) => {
	return {
		onDisablePullToRefresh: (pullToRefreshDisabled: boolean) =>
			dispatch(actions.disablePullToRefresh(pullToRefreshDisabled)),
		onGetBounce: (bounce: boolean) => dispatch(actions.getBounce(bounce)),
		onGetContent: (data: IContent) => dispatch(actions.getContent(data)),
		onMediaLoaded: (mediaLoaded: boolean) =>
			dispatch(actions.setMediaLoaded(mediaLoaded)),
		onNavigationFinished: () => dispatch(actions.navigationFinished()),
		onScrollFinished: () => dispatch(actions.scrollFinished()),
		onSetScrollDirection: (scrollDirection: "up" | "down") =>
			dispatch(actions.setScrollDirection(scrollDirection)),
		onToggleSideDrawer: (val: boolean) =>
			dispatch(actions.toggleSideDrawer(val)),
		onUnmuted: (muted: boolean) => dispatch(actions.toggleSound(muted)),
		onVideoLoaded: (videoLoaded: boolean) =>
			dispatch(actions.videoIsLoaded(videoLoaded))
	};
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ScrollyContainer);



// WEBPACK FOOTER //
// ./src/containers/ScrollyContainer.tsx